/**
 * Step 5: Editing (Manual Selection)
 * User can select/deselect domains, URLs, and phones
 */

import { useState } from 'react'
import {
  Space,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Checkbox,
  Tag,
  Input,
  Divider,
  Popconfirm,
  Button,
  Tooltip,
  Empty,
  Typography,
  Slider,
  InputNumber,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  GlobalOutlined,
  LinkOutlined,
  PhoneOutlined,
} from '@ant-design/icons'
import type { DomainWithAnalysis, ParsingResult, InputMethod } from '../types'
import { normalizePhone } from '../utils/phoneUtils'
import { isPureDomain } from '../utils'

const { Text, Title } = Typography
const { Search } = Input

interface EditingStepProps {
  projectName?: string | null
  inputMethod: InputMethod | null
  domains: DomainWithAnalysis[]
  parsingResult: ParsingResult | null
  selectedPhones: Set<string>
  searchText: string
  onSearchTextChange: (text: string) => void
  onToggleDomainSelection: (fullUrl: string) => void
  onRemoveDomain: (fullUrl: string) => void
  onSetDomains: (setter: (prev: DomainWithAnalysis[]) => DomainWithAnalysis[]) => void
  onSetSelectedPhones: (phones: Set<string>) => void
}

export default function EditingStep({
  projectName,
  inputMethod,
  domains,
  parsingResult,
  selectedPhones,
  searchText,
  onSearchTextChange,
  onToggleDomainSelection,
  onRemoveDomain,
  onSetDomains,
  onSetSelectedPhones,
}: EditingStepProps) {
  // Minimum AI score threshold for filtering
  const [minAiScore, setMinAiScore] = useState<number>(70)

  // Filter domains by search
  const filteredDomains = searchText
    ? domains.filter((d) => d.host.toLowerCase().includes(searchText.toLowerCase()))
    : domains

  // Separate pure domains and pages
  const pureDomains = filteredDomains.filter(d => isPureDomain(d.full_url))
  const pagesWithPaths = filteredDomains.filter(d => !isPureDomain(d.full_url))

  // Count selected
  const selectedDomainsCount = pureDomains.filter(d => d.selected).length
  const selectedPagesCount = pagesWithPaths.filter(d => d.selected).length
  const phonesCount = parsingResult?.phones?.length || 0
  const selectedPhonesCount = selectedPhones.size

  // Total stats
  const totalItems = pureDomains.length + pagesWithPaths.length + phonesCount
  const totalSelected = selectedDomainsCount + selectedPagesCount + selectedPhonesCount
  const selectedCount = domains.filter(d => d.selected).length
  const isManualInput = inputMethod === 'manual'

  // Create columns for tables
  const createColumns = (
    type: 'domains' | 'pages',
    dataSource: DomainWithAnalysis[],
    selectedCountForType: number
  ): ColumnsType<DomainWithAnalysis> => [
    {
      title: (
        <Checkbox
          checked={selectedCountForType === dataSource.length && dataSource.length > 0}
          indeterminate={selectedCountForType > 0 && selectedCountForType < dataSource.length}
          onChange={(e) => {
            // Use full_url as unique identifier
            const urls = new Set(dataSource.map(d => d.full_url))
            onSetDomains(prev => prev.map(d => ({
              ...d,
              selected: urls.has(d.full_url) ? e.target.checked : d.selected
            })))
          }}
        />
      ),
      key: 'checkbox',
      width: 50,
      render: (_: any, record: DomainWithAnalysis) => (
        <Checkbox checked={record.selected} onChange={() => onToggleDomainSelection(record.full_url || '')} />
      ),
    },
    {
      title: type === 'domains' ? 'Домен' : 'Ссылка',
      dataIndex: 'host',
      key: 'host',
      ellipsis: true,
      render: (_: string, record: DomainWithAnalysis) => {
        const displayText = type === 'pages' && record.full_url ? record.full_url : record.host
        const url = record.full_url || (record.host.startsWith('http') ? record.host : `https://${record.host}`)
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
            {displayText}
          </a>
        )
      },
    },
    {
      title: 'AI Оценка',
      key: 'ai_result',
      width: 120,
      render: (_: any, record: DomainWithAnalysis) => {
        if (record.ai_score === undefined) return <Tag color="default">—</Tag>

        const getColor = () => {
          if (record.ai_recommendation === 'approved') return 'success'
          if (record.ai_recommendation === 'rejected') return 'error'
          return 'warning'
        }

        return (
          <Tooltip title={record.ai_reason}>
            <Tag color={getColor()}>{record.ai_score}/100</Tag>
          </Tooltip>
        )
      },
    },
    {
      title: 'Клики',
      dataIndex: 'total_clicks',
      key: 'total_clicks',
      width: 80,
      render: (value: number) => value?.toLocaleString() || '—',
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: any, record: DomainWithAnalysis) => (
        <Popconfirm
          title={`Удалить ${type === 'domains' ? 'домен' : 'ссылку'}?`}
          onConfirm={() => onRemoveDomain(record.full_url || '')}
          okText="Да"
          cancelText="Нет"
        >
          <Button type="text" danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ]

  // Action buttons for tables
  const renderActionButtons = (
    type: 'domains' | 'pages' | 'phones',
    dataSource: DomainWithAnalysis[]
  ) => (
    <Space wrap size="small">
      <Button
        size="small"
        type="default"
        onClick={() => {
          if (type === 'phones') {
            const allPhones = parsingResult?.phones?.map((p: any) => normalizePhone(p.phone_number)).filter(Boolean) as string[]
            onSetSelectedPhones(new Set(allPhones))
          } else {
            // Use full_url as unique identifier
            const urls = new Set(dataSource.map(d => d.full_url))
            onSetDomains(prev => prev.map(d => ({
              ...d,
              selected: urls.has(d.full_url) ? true : d.selected
            })))
          }
        }}
      >
        Выбрать все
      </Button>
      <Button
        size="small"
        type="default"
        onClick={() => {
          if (type === 'phones') {
            onSetSelectedPhones(new Set())
          } else {
            // Use full_url as unique identifier
            const urls = new Set(dataSource.map(d => d.full_url))
            onSetDomains(prev => prev.map(d => ({
              ...d,
              selected: urls.has(d.full_url) ? false : d.selected
            })))
          }
        }}
      >
        Снять все
      </Button>
      {type !== 'phones' && (
        <>
          <Divider type="vertical" />
          <Space size="small">
            <span style={{ fontSize: 12, color: '#666' }}>AI ≥</span>
            <InputNumber
              size="small"
              min={0}
              max={100}
              value={minAiScore}
              onChange={(value) => setMinAiScore(value ?? 70)}
              style={{ width: 60 }}
            />
            <Slider
              min={0}
              max={100}
              value={minAiScore}
              onChange={setMinAiScore}
              style={{ width: 100, margin: '0 8px' }}
              tooltip={{ formatter: (value) => `${value}%` }}
            />
            <Button
              size="small"
              type="primary"
              ghost
              icon={<CheckCircleOutlined />}
              onClick={() => {
                // Use full_url as unique identifier
                const urls = new Set(dataSource.map(d => d.full_url))
                onSetDomains(prev => prev.map((d) => ({
                  ...d,
                  selected: urls.has(d.full_url)
                    ? (d.ai_score !== undefined && d.ai_score >= minAiScore)
                    : d.selected
                })))
              }}
            >
              Применить
            </Button>
          </Space>
        </>
      )}
    </Space>
  )

  // Render a source table section
  const renderSourceTable = (
    type: 'domains' | 'pages',
    dataSource: DomainWithAnalysis[],
    selectedCountForType: number,
    icon: React.ReactNode,
    color: string
  ) => {
    if (dataSource.length === 0) return null

    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          padding: '8px 12px',
          background: '#fafafa',
          borderRadius: 6
        }}>
          <Space>
            {icon}
            <Text strong>{type === 'domains' ? 'Домены' : 'Ссылки'}</Text>
            <Tag color={color}>{selectedCountForType} / {dataSource.length}</Tag>
          </Space>
          {renderActionButtons(type, dataSource)}
        </div>
        <Table
          dataSource={dataSource}
          columns={createColumns(type, dataSource, selectedCountForType)}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            size: 'small',
          }}
          size="small"
          rowKey="full_url"
        />
      </div>
    )
  }

  // Render phones table
  const renderPhonesTable = () => {
    if (!parsingResult?.phones || parsingResult.phones.length === 0) return null

    const allPhones = parsingResult.phones.map((p: any) => normalizePhone(p.phone_number)).filter(Boolean) as string[]
    const allSelected = allPhones.length > 0 && allPhones.every(p => selectedPhones.has(p))
    const someSelected = selectedPhonesCount > 0 && !allSelected

    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          padding: '8px 12px',
          background: '#fafafa',
          borderRadius: 6
        }}>
          <Space>
            <PhoneOutlined style={{ color: '#fa8c16' }} />
            <Text strong>Телефоны</Text>
            <Tag color="orange">{selectedPhonesCount} / {phonesCount}</Tag>
          </Space>
          {renderActionButtons('phones', [])}
        </div>
        <Table
          dataSource={parsingResult.phones}
          columns={[
            {
              title: (
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onSetSelectedPhones(new Set(allPhones))
                    } else {
                      onSetSelectedPhones(new Set())
                    }
                  }}
                />
              ),
              key: 'checkbox',
              width: 50,
              render: (_: any, record: any) => {
                const normalized = normalizePhone(record.phone_number)
                return (
                  <Checkbox
                    checked={normalized ? selectedPhones.has(normalized) : false}
                    onChange={() => {
                      if (!normalized) return
                      const newSet = new Set(selectedPhones)
                      if (newSet.has(normalized)) {
                        newSet.delete(normalized)
                      } else {
                        newSet.add(normalized)
                      }
                      onSetSelectedPhones(newSet)
                    }}
                  />
                )
              },
            },
            {
              title: 'Исходный номер',
              dataIndex: 'phone_number',
              key: 'phone_number_raw',
              width: 180,
            },
            {
              title: 'Нормализованный',
              dataIndex: 'phone_number',
              key: 'phone_number_normalized',
              render: (phone: string) => {
                const normalized = normalizePhone(phone)
                return (
                  <span style={{ fontFamily: 'monospace', color: '#52c41a', fontWeight: 500 }}>
                    {normalized}
                  </span>
                )
              },
            },
          ]}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            size: 'small',
          }}
          size="small"
          rowKey={(_, index) => `phone_${index}`}
        />
      </div>
    )
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Header Card with gradient */}
      <Card
        bordered={false}
        style={{
          background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
          color: 'white'
        }}
      >
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space direction="vertical" size={4}>
              <Title level={4} style={{ margin: 0, color: 'white' }}>
                <EditOutlined /> Редактирование источников
              </Title>
              <Text style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                {projectName || 'Проект не выбран'}
              </Text>
            </Space>
          </Col>
          <Col>
            <Search
              placeholder="Поиск..."
              allowClear
              value={searchText}
              onChange={(e) => onSearchTextChange(e.target.value)}
              style={{ width: 250 }}
            />
          </Col>
        </Row>
      </Card>

      {/* Statistics Row */}
      <Row gutter={16}>
        <Col span={pureDomains.length > 0 ? (pagesWithPaths.length > 0 ? (phonesCount > 0 ? 6 : 8) : (phonesCount > 0 ? 8 : 12)) : 0}>
          {pureDomains.length > 0 && (
            <Card size="small" style={{ background: selectedDomainsCount > 0 ? '#f6ffed' : '#fafafa' }}>
              <Statistic
                title={<span><GlobalOutlined /> Домены</span>}
                value={selectedDomainsCount}
                suffix={`/ ${pureDomains.length}`}
                valueStyle={{ color: selectedDomainsCount > 0 ? '#52c41a' : undefined }}
              />
            </Card>
          )}
        </Col>
        <Col span={pagesWithPaths.length > 0 ? (pureDomains.length > 0 ? (phonesCount > 0 ? 6 : 8) : (phonesCount > 0 ? 8 : 12)) : 0}>
          {pagesWithPaths.length > 0 && (
            <Card size="small" style={{ background: selectedPagesCount > 0 ? '#e6f7ff' : '#fafafa' }}>
              <Statistic
                title={<span><LinkOutlined /> Ссылки</span>}
                value={selectedPagesCount}
                suffix={`/ ${pagesWithPaths.length}`}
                valueStyle={{ color: selectedPagesCount > 0 ? '#1890ff' : undefined }}
              />
            </Card>
          )}
        </Col>
        <Col span={phonesCount > 0 ? (pureDomains.length > 0 ? (pagesWithPaths.length > 0 ? 6 : 8) : (pagesWithPaths.length > 0 ? 8 : 12)) : 0}>
          {phonesCount > 0 && (
            <Card size="small" style={{ background: selectedPhonesCount > 0 ? '#fff7e6' : '#fafafa' }}>
              <Statistic
                title={<span><PhoneOutlined /> Телефоны</span>}
                value={selectedPhonesCount}
                suffix={`/ ${phonesCount}`}
                valueStyle={{ color: selectedPhonesCount > 0 ? '#fa8c16' : undefined }}
              />
            </Card>
          )}
        </Col>
        <Col span={pureDomains.length > 0 || pagesWithPaths.length > 0 || phonesCount > 0 ? 6 : 24}>
          <Card size="small" style={{ background: totalSelected > 0 ? '#f9f0ff' : '#fafafa' }}>
            <Statistic
              title={<span><CheckCircleOutlined /> Всего выбрано</span>}
              value={totalSelected}
              suffix={`/ ${totalItems}`}
              valueStyle={{ color: totalSelected > 0 ? '#722ed1' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content Card */}
      <Card size="small">
        {/* Domains Table */}
        {renderSourceTable(
          'domains',
          pureDomains,
          selectedDomainsCount,
          <GlobalOutlined style={{ color: '#52c41a' }} />,
          'green'
        )}

        {/* Pages/URLs Table */}
        {renderSourceTable(
          'pages',
          pagesWithPaths,
          selectedPagesCount,
          <LinkOutlined style={{ color: '#1890ff' }} />,
          'blue'
        )}

        {/* Phones Table */}
        {renderPhonesTable()}

        {/* Empty state */}
        {totalItems === 0 && (
          <Empty description="Нет данных для редактирования" />
        )}
      </Card>

      {/* Summary Alert */}
      <Alert
        message={
          <Space split={<Divider type="vertical" />}>
            <span>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
              <strong>Выбрано для загрузки:</strong>
            </span>
            {(isManualInput ? pureDomains.length > 0 : filteredDomains.length > 0) && (
              <span>домены: <strong>{isManualInput ? selectedDomainsCount : selectedCount}</strong></span>
            )}
            {isManualInput && pagesWithPaths.length > 0 && (
              <span>ссылки: <strong>{selectedPagesCount}</strong></span>
            )}
            {phonesCount > 0 && (
              <span>телефоны: <strong>{selectedPhonesCount}</strong></span>
            )}
          </Space>
        }
        type={totalSelected > 0 ? 'success' : 'warning'}
        showIcon
      />
    </Space>
  )
}
